

import { useState, useEffect } from "react";
import { useToast } from "@/context/ToastContext";
import { eventsApi, vendorsApi } from "@/lib/api";
import SearchableDropdown from "../../components/SearchableDropdown";

interface VendorOption {
  id: number;
  name: string;
  vendor_type?: {
    id: number;
    name: string;
  };
  commission_type?: string;
  commission_value?: number;
}

interface AddVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  onVendorAdded: () => void;
}

const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

export default function AddVendorModal({
  isOpen,
  onClose,
  eventId,
  onVendorAdded,
}: AddVendorModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [commissionAutoFilled, setCommissionAutoFilled] = useState(false);

  // Form state - matching quotation style
  const [formData, setFormData] = useState({
    vendor_id: undefined as number | undefined,
    description: "",
    quantity: 1,
    unit: "unit",
    unit_price: 0,
    cost_price: 0,
    commission_value: 0,
    commission_type: "percentage",
    notes: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});

  // Fetch vendors when modal opens
  useEffect(() => {
    if (isOpen) {
      loadVendors();
      resetForm();
    }
  }, [isOpen]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorsApi.list('for_select=true');
      // Handle different API response structures
      let vendorData = [];
      if (Array.isArray(response)) {
        vendorData = response;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        vendorData = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        vendorData = response.data;
      } else if (response?.vendors && Array.isArray(response.vendors)) {
        vendorData = response.vendors;
      }
      setVendors(vendorData);
    } catch (error) {
      console.error("Error loading vendors:", error);
      showToast("Failed to load vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: undefined,
      description: "",
      quantity: 1,
      unit: "unit",
      unit_price: 0,
      cost_price: 0,
      commission_value: 0,
      commission_type: "percentage",
      notes: "",
    });
    setErrors({});
    setCommissionAutoFilled(false);
  };

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleVendorSelect = (vendorId: string) => {
    const vendor = vendors.find(v => String(v.id) === vendorId);
    if (vendor) {
      const hasStoredCommission = (vendor.commission_value ?? 0) > 0;
      setFormData(prev => ({
        ...prev,
        vendor_id: vendor.id,
        commission_value: vendor.commission_value ?? 0,
        commission_type: vendor.commission_type || "percentage",
      }));
      setCommissionAutoFilled(hasStoredCommission);
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};

    if (!formData.vendor_id) newErrors.vendor_id = "Vendor is required";
    if (!formData.description?.trim()) newErrors.description = "Service description is required";
    if (formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
    if (formData.unit_price < 0) newErrors.unit_price = "Price cannot be negative";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateCost = () => {
    const unitPrice = formData.unit_price || 0;
    const commissionValue = formData.commission_value ?? 0;
    const commissionType = formData.commission_type || "percentage";

    let commission = 0;
    if (commissionType === "percentage") {
      commission = (unitPrice * commissionValue) / 100;
    } else {
      commission = commissionValue;
    }

    return unitPrice - commission;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const vendorCost = calculateCost();
    const totalAmount = vendorCost * formData.quantity;

    setSubmitting(true);
    try {
      await eventsApi.addEventVendor(eventId, {
        vendor_id: formData.vendor_id,
        description: formData.description,
        quantity: formData.quantity,
        unit: formData.unit,
        unit_price: formData.unit_price,
        cost_price: vendorCost,
        total_amount: totalAmount,
        commission_value: formData.commission_value,
        commission_type: formData.commission_type,
        notes: formData.notes,
      });

      showToast("Vendor added successfully", "success");
      onClose();
      onVendorAdded();
    } catch (error: any) {
      showToast(error?.message || "Failed to add vendor", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const lineTotal = formData.quantity * formData.unit_price;
  const vendorCost = calculateCost();
  const totalVendorCost = vendorCost * formData.quantity;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/60 backdrop-blur-sm">
      <div className="w-full h-full bg-white flex flex-col md:max-w-2xl md:mx-auto md:my-8 md:rounded-2xl md:shadow-2xl md:overflow-hidden md:h-auto md:max-h-[90vh]">
        {/* Header */}
        <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-blue-50 to-white flex-shrink-0 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-stone-800">Add Vendor</h3>
            <p className="text-[10px] text-stone-500">Assign vendor with pricing</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Vendor & Service Row */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Vendor *</label>
              {loading ? (
                <div className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-400">
                  Loading vendors...
                </div>
              ) : (
                <SearchableDropdown
                  options={vendors.map(v => ({ value: String(v.id), label: v.name }))}
                  value={formData.vendor_id ? String(formData.vendor_id) : ""}
                  onChange={handleVendorSelect}
                  placeholder="Select vendor"
                />
              )}
              {errors.vendor_id && <p className="text-xs text-rose-500 mt-0.5">{errors.vendor_id}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Service *</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="e.g., Photography, Catering"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
              {errors.description && <p className="text-xs text-rose-500 mt-0.5">{errors.description}</p>}
            </div>
          </div>

          {/* Quantity, Unit, Price Row */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Qty *</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)}
                className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
              {errors.quantity && <p className="text-xs text-rose-500 mt-0.5">{errors.quantity}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => handleChange("unit", e.target.value)}
                placeholder="unit"
                className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Unit Price *</label>
              <div className="relative">
                <INRIcon className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-xs" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => handleChange("unit_price", parseFloat(e.target.value) || 0)}
                  className="w-full pl-5 pr-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              {errors.unit_price && <p className="text-xs text-rose-500 mt-0.5">{errors.unit_price}</p>}
            </div>
          </div>

          {/* Commission */}
          {commissionAutoFilled && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Commission auto-filled from vendor profile
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Comm. Type</label>
              <select
                value={formData.commission_type}
                onChange={(e) => handleChange("commission_type", e.target.value)}
                className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
              >
                <option value="percentage">%</option>
                <option value="fixed">₹</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">
                {formData.commission_type === "percentage" ? "Comm. %" : "Comm. ₹"}
              </label>
              <div className="relative">
                {formData.commission_type === "fixed" && (
                  <INRIcon className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-xs" />
                )}
                <input
                  type="number"
                  min="0"
                  step={formData.commission_type === "percentage" ? "1" : "0.01"}
                  max={formData.commission_type === "percentage" ? "100" : undefined}
                  value={formData.commission_value ?? 0}
                  onChange={(e) => handleChange("commission_value", parseFloat(e.target.value) || 0)}
                  className={`w-full ${formData.commission_type === "fixed" ? "pl-5" : "pl-2"} pr-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none`}
                />
              </div>
            </div>
          </div>

          {/* Calculations */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-stone-50 rounded-lg p-2 border border-stone-200">
              <p className="text-stone-500 mb-0.5">Vendor Cost/Unit</p>
              <p className="font-semibold text-rose-600">₹{vendorCost.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-stone-50 rounded-lg p-2 border border-stone-200">
              <p className="text-stone-500 mb-0.5">Margin</p>
              <p className="font-semibold text-emerald-600">₹{(lineTotal - totalVendorCost).toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
              <p className="text-[10px] text-stone-500 uppercase">Client Total</p>
              <p className="text-base font-bold text-amber-700">₹{lineTotal.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-center">
              <p className="text-[10px] text-stone-500 uppercase">Vendor Total</p>
              <p className="text-base font-bold text-rose-600">₹{totalVendorCost.toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Special requirements..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-4 py-3 flex gap-2 flex-shrink-0">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting ? "Adding..." : "Add Vendor"}
          </button>
        </div>
      </div>
    </div>
  );
}
